import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Filter as FilterIcon } from 'lucide-react';

import { paths } from 'configuration/paths';
import { TranslationsKeys } from 'types/translation';
import { Survey } from 'types/survey';
import { Status, SurveyResponse } from 'types/surveyResponse';
import { Visit as VisitType, VisitCustomer } from 'types/customer';
import { useTranslate } from 'hooks/useTranslate';
import { usePagination } from 'hooks/usePagination';
import { SearchInput } from 'components/ui/SearchInput';
import { Spinner } from 'components/Spinner';
import { Pagination } from 'components/Pagination';
import { FilterValues, SurveyResponsesFilters } from './SurveyResponsesFilters';
import { Visit } from './Visit';
import styles from '../MySurveys.module.scss';

export interface IProps {
  isCompleted: boolean;
  surveyDefinitions: Survey[];
  surveyResponses: SurveyResponse[];
  loading: boolean;
}

// to be shown for survey responses that don't have visit properly recorded
// (older survey responses basically)

const dummyCustomer: VisitCustomer = {
  companyName: 'No visit data',
  businessAreaCode: '',
  city: '',
  country: '',
  contactName: 'No customer data',
  address: '',
  sector: '',
  customerId: '',
};

const dummyVisit: VisitType = {
  visitId: '',
  startDate: '2000-01-01',
  endDate: '2099-12-31',
  owner: { email: 'unknown@unknown', name: 'N/A' },
  attendees: [],
  visitTypeCode: ''
};

export const SurveyResponses = ({ isCompleted, surveyDefinitions, surveyResponses, loading }: IProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [visitQuery, setVisitQuery] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [filter, setFilter] = useState<FilterValues>({});
  
  const numSurveyResponses = useMemo(() => surveyResponses.filter(sr =>
    isCompleted ? sr.status === Status.Complete : sr.status === Status.NotStarted || sr.status === Status.InProgress).length, [surveyResponses, isCompleted]);
  const visibleSurveyResponses = useMemo(() => {
    // completed or in progress
    let result = surveyResponses.filter(sr =>
      isCompleted ? sr.status === Status.Complete : sr.status === Status.NotStarted || sr.status === Status.InProgress);
    
    // filters
    if (filter.businessArea) {
      result = result.filter(sr => sr.customer?.businessAreaCode === filter.businessArea);
    }
    if (filter.country) {
      result = result.filter(sr => sr.customer?.country === filter.country);
    }
    if (filter.visitType) {
      result = result.filter(sr => sr.visit?.visitTypeCode === filter.visitType);
    }
    
    // search box
    if (visitQuery) {
      result = result.filter((sr) => {
        return (
          sr.customer?.companyName.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.contactName.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.address.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.city.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.country.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.state?.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.sector.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.businessAreaCode?.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.customer?.customerId.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.visit?.startDate.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.visit?.visitTypeCode.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.visit?.owner.name?.toLowerCase().includes(visitQuery.toLowerCase()) ||
          sr.visit?.attendees.some((attendee) =>
            attendee.name?.toLowerCase().includes(visitQuery.toLowerCase())
          ) ||
          surveyDefinitions.find(sd => sd.surveyDefinitionId === sr.surveyDefinitionId)?.name.toLowerCase().includes(visitQuery.toLowerCase())
        );
      });
    }
    
    // sort by newest first
    result = result.sort((a, b) => {
      if (a.createdAt < b.createdAt) return 1;
      if (a.createdAt > b.createdAt) return -1;
      return 0;
    });
    return result;
  }, [surveyResponses, surveyDefinitions, filter, visitQuery, isCompleted]);

  const [page, setPage] = useState(1);
  const { items: paginatedSurveyResponses, pages } = usePagination(visibleSurveyResponses, page);

  const getVisitData = (surveyResponse: SurveyResponse) => {
    // use visit data that is stored in survey response now, but fallback to empty records if not available
    const visit = { customer: surveyResponse.customer || dummyCustomer, plannedVisit: surveyResponse.visit || dummyVisit };
    // if we fell back but the older customerName field is set, use that at least
    if (visit.customer.companyName === 'No visit data' && surveyResponse.customerName) {
      visit.customer = { ...visit.customer, companyName: visit.customer.companyName, customerId: visit.customer.customerId };
    }
    return visit;
  };

  return (
    <div>
      <h1 className={styles.mySurveysHeading}>
        {t(isCompleted ? TranslationsKeys.mySurveysPage_completedVisits_title : TranslationsKeys.mySurveysPage_inProgressVisits_title)}
        {' '}
        {!loading && surveyResponses && <>({numSurveyResponses})</>}
      </h1>
      <div className={styles.mySurveysSearchForm}>
        <SearchInput
          variant="inline"
          onChange={(query) => {
            setVisitQuery(query);
            setPage(1);
          }}
        />
        <div className={styles.mySurveysSearchFormInfoActions}>
          <div className={styles.mySurveysSearchFormResultCount}>
            {!visitQuery && !Object.values(filter).some(Boolean)
              ? t(TranslationsKeys.common_all)
              : `${visibleSurveyResponses.length} ${t(TranslationsKeys.common_results).toLowerCase()}`}
          </div>
          <button className={styles.mySurveysSearchFormFilter} onClick={() => setShowFilters(true)}>
            <FilterIcon/>
            {Object.values(filter).some(Boolean)
              ? t(TranslationsKeys.mySurveysPage_search_filtersToggle, {
                activeFiltersCount: Object.values(filter).filter(Boolean).length.toString()
              })
              : t(TranslationsKeys.common_filters)}
          </button>
        </div>
      </div>
      {loading && (
        <div className={styles.mySurveysSpinner}>
          <Spinner size="large"/>
        </div>
      )}
      {!loading &&
        paginatedSurveyResponses?.length > 0 &&
        paginatedSurveyResponses.map((sr) => <Visit
          key={sr.responseId}
          surveyDefinition={surveyDefinitions.find(sd => sd.surveyDefinitionId === sr.surveyDefinitionId)}
          visit={getVisitData(sr)} 
          visitQuery={visitQuery}
          buttonLabel={t(isCompleted ? TranslationsKeys.common_view : TranslationsKeys.common_edit)}
          buttonClass={isCompleted ? "secondary" : "primary"}
          onSelect={() => navigate((isCompleted ? paths.viewSurvey : paths.survey).replace(':surveyId', sr.responseId))}
        />)}
      {!loading && <Pagination page={page} pages={pages} onSelectPage={setPage} />}
      {showFilters && 
        <SurveyResponsesFilters
          filter={filter}
          allSurveys={surveyResponses}
          onFilterChange={(filter) => {
            setFilter(filter);
            setPage(1);
            setShowFilters(false);
          }}
          onClose={() => setShowFilters(false)}
      />}
    </div>
  );
};
